import { Engagement, Termination, StaffNote } from "@oysterhr/schema"
import { useForm, FieldValues, Controller } from "react-hook-form"
import { Button, Modal, TextArea, FormLabel, Avatar, Toast, useToast } from "@oysterhr/ui"
import { useTranslation } from "react-i18next"
import { useMutation } from "@oysterhr/core"
import { useMemo } from "react"
import * as Yup from "yup"
import { yupResolver } from "@hookform/resolvers/yup"
import { gql } from "@apollo/client"
import { toLuxonDateTime } from "@oysterhr/common"

type PayrollProcessingDetailsModalProps = {
  setEditing: (val: boolean) => void
  engagement: Engagement
  termination: Termination
  notes: StaffNote[]
}

interface FormData {
  note: string
}

const POST_MESSAGE_MUTATION = gql`
  mutation PostPayrollSpecialistNote($input: PostStaffNoteInput!) {
    postStaffNote(input: $input) {
      postingContextNotes {
        id
        totalCount
        nodes {
          id
          createdAt
          createdBy {
            id
            name
            avatarUrl
          }
          content
        }
      }
    }
  }
`

export const PayrollProcessingDetailsModal = ({
  setEditing,
  termination,
  notes,
}: PayrollProcessingDetailsModalProps) => {
  const { t } = useTranslation("colleague_interface/EmployeeProfilePage", {
    keyPrefix: "offboarding.offboardingSummary.payrollProcessingDetails",
  })

  const toast = useToast()

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        note: Yup.string().required(t("noteRequired")),
      }) as Yup.Schema<FormData>,
    [t],
  )

  const { control, handleSubmit } = useForm<FormData>({
    resolver: yupResolver(validationSchema),
  })

  const [postMessage] = useMutation(POST_MESSAGE_MUTATION)

  const postingContext = termination?.payrollSpecialistNotes?.postingContext

  const sendMessage = async (data: FieldValues) => {
    await postMessage({
      variables: {
        input: {
          postingContext,
          content: data.note,
        },
      },
      onCompleted: () => {
        setEditing(false)
        toast({
          render: (
            <Toast variation="success">
              <div data-testid="payroll-processing-details-toast">
                <Toast.Content>{t("successToast")}</Toast.Content>
              </div>
            </Toast>
          ),
        })
      },
    })
  }

  return (
    <Modal
      isOpen={true}
      onRequestClose={() => setEditing(false)}
      size="md"
      data-testid="payroll-processing-details-modal"
    >
      <Modal.Header>{t("edit")}</Modal.Header>
      <Modal.Content className="flex flex-col space-y-2">
        <Controller
          control={control}
          name="note"
          render={({ field, fieldState: { error } }) => (
            <div className="flex flex-col gap-1.5">
              <TextArea
                {...field}
                label={<FormLabel htmlFor="notes-input" label="Comments" />}
                placeholder="Add comments"
                error={error?.message}
              />
            </div>
          )}
        />
        <Notes notes={notes} />
      </Modal.Content>
      <Modal.Footer>
        <Button variation="secondary" onClick={() => setEditing(false)}>
          Cancel
        </Button>
        <Button onClick={handleSubmit(sendMessage)}>Save</Button>
      </Modal.Footer>
    </Modal>
  )
}

const Notes = ({ notes }: { notes: StaffNote[] }) => {
  const { t } = useTranslation("colleague_interface/EmployeeProfilePage", {
    keyPrefix: "offboarding.offboardingSummary.payrollProcessingDetails",
  })

  return (
    <>
      <FormLabel label="Comment history" />
      {!!notes && notes.length !== 0 ? (
        <div
          style={{
            backgroundColor: "#F5F5F5",
            display: "flex",
            padding: "16px",
            flexDirection: "column",
            alignItems: "flex-start",
            gap: "16px",
            borderRadius: "8px",
            alignSelf: "stretch",
          }}
        >
          {notes.map((note: StaffNote) => (
            <Note key={note.id} note={note} />
          ))}
        </div>
      ) : (
        t("emptyComments")
      )}
    </>
  )
}

const Note = ({ note }: { note: StaffNote }) => {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "row",
        alignItems: "flex-start",
        gap: "4px",
        flex: "1 0 0",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          borderRadius: "8px",
          alignSelf: "stretch",
        }}
      >
        <div className="mt-1 pr-4">
          <Avatar src={note.createdBy.avatarUrl} name={note.createdBy.name} />
        </div>
        <div>
          <span className="text-sm font-semibold">{note.createdBy.name} </span>
          <span className="text-xs italic text-gray-400">
            {toLuxonDateTime(note.createdAt).toLocaleString({
              day: "2-digit",
              month: "short",
              year: "numeric",
              hour: "2-digit",
              minute: "2-digit",
            })}
          </span>
          <div className="mt-2">{note.content}</div>
        </div>
      </div>
    </div>
  )
}
